/**
 * Page copy for /industries/pharma.
 *
 * Source: page-industry-pharma.md, supplied by the client.
 *
 * Decisions taken with the client:
 *
 * 1. §7 PROOF. The source offers the AI Documentation case study — 70%
 *    documentation debt, 85% documentation kept current — as an option, flagged
 *    as manufacturing rather than pharma. Client chose to leave it out. Those
 *    two figures appear nowhere on this page and are not approved metrics.
 * 2. HUB. This page answers the open question on /industries: WeAreQA does take
 *    on validation beyond Part 11 controls — CSV lifecycle, GAMP 5
 *    categorisation, SOP alignment. The hub's "Pharma engagements beyond
 *    Part 11" exclusion and its placeholder are removed accordingly.
 * 3. §3 AND §4 REGULATORY. Built verbatim, and flagged in CLAUDE.md. The source
 *    calls §3 "the only content on the site with a shelf life" and asks for a
 *    quarterly re-check.
 * 4. SERVICE LINKS. The source's hrefs (/services/qa-consulting,
 *    /services/ai-testing-services) are earlier slugs; repointed at the routes
 *    that exist. /ai-native-delivery-framework does not exist and is held at "#".
 *
 * BUYER. This page is deliberately not written for a CTO — validation sits under
 * Quality, and Quality holds a veto Engineering doesn't. The site-wide primary
 * CTA still reads "Schedule an Engineering Call"; that is approved copy used on
 * every page and is left alone here.
 *
 * NO PLACEHOLDERS LEFT (2026-08-19). The price slot was removed and §3's CSA
 * status was verified. See the notes at each.
 */

import type { IconName } from "../../components/ui/waq/Icon";

export const seo = {
  title: "Computer System Validation & CSA Services for Pharma",
  description:
    "Risk-based computer software assurance for eQMS, ERP and clinical systems. 21 CFR Part 11, GAMP 5, and a documented rationale that survives inspection.",
} as const;

export const hero = {
  breadcrumb: { label: "Industries", href: "/industries" },
  badge: "Pharma",
  heading: "Computer System Validation & Software Assurance",
  situation:
    "Validating a system takes months, costs more than the system, and produces a binder nobody opens until an inspector asks for it. So changes get avoided, and the systems stay old — which is its own risk.",
  /** The turn. Its own line in the source, and the reason the page exists. */
  turn: "You already know CSA exists. The problem isn't the guidance.",
  scopeLabel: "Scope",
  scope: ["eQMS", "Document management", "ERP", "Clinical systems"],
} as const;

/**
 * §2 — the page. Every Quality lead has read the guidance; none needs it
 * explained. What blocks them is that adopting it requires a personal signature
 * on a defensibility judgement. Naming that is the only thing here a competitor
 * is not also saying.
 */
export const stall = {
  heading: "Why haven't we moved from CSV to CSA?",
  display: "The real reason CSA adoption stalls",
  line: "Nobody has ever been written up for over-documenting.",
  blocked:
    "Moving to a risk-based approach means somebody signs the decision that less documentation is defensible for a given system. That person is the one standing in front of an inspector eighteen months later, explaining why a function was tested with a scenario rather than a scripted protocol. Meanwhile the SOP still says CSV, and rewriting the SOP requires the same signature.",
  consequence:
    "So the guidance moved and practice didn't. Most quality organisations are still applying full CSV to a SaaS document management system, because that's what the SOP says and nobody wants to be the person who changed it.",
  unblock: {
    strong: "The thing that unblocks it isn't courage. It's a documented rationale that holds up.",
    tail: " A risk assessment an inspector can follow, tied to intended use and process risk, with the assurance activity proportionate to what was assessed. Once that exists and is defensible, signing it is a normal decision rather than a career one.",
  },
  close: "That's what we build.",
} as const;

/**
 * §3 — dated regulatory claims. The source calls this the only content on the
 * site with a shelf life. The "what CSA is not" block is the credibility test:
 * every vendor in this space sells CSA as cost reduction.
 */
export const csa = {
  heading: "What is Computer Software Assurance and how is it different from CSV?",
  display: "What CSA actually changed",
  regulatory:
    "FDA finalised Computer Software Assurance guidance in September 2025, and issued an updated version in February 2026 aligned to the Quality Management System Regulation, which took effect on 2 February 2026 and brings ISO 13485:2016 into 21 CFR 820 by reference.",
  shift: {
    lead: "The shift is from uniform documentation to proportionate assurance. Effort follows intended use and process risk. ",
    strong:
      "High process risk gets scripted or hybrid testing. Risks that aren't high can be covered with leaner methods — including exploratory and scenario testing.",
    tail: " Vendor test evidence becomes usable for lower-risk functions rather than something you repeat yourself.",
  },
  clarifications: [
    {
      label: "What CSA is not.",
      body: "It is not permission to test less. High-risk production software receives more scrutiny under CSA, not less — the change is where the effort goes, not how much there is. Anyone selling CSA as a cost reduction has misread it, and your inspector has not.",
      /** "receives *more* scrutiny" — the emphasis is the source's own. */
      emphasis: "more",
      lead: true,
    },
    {
      label: "What hasn't changed.",
      body: "Part 11 controls stand exactly where they were: audit trails, electronic signatures, access controls, data integrity. CSA changes the assurance approach around them, not the controls themselves.",
      emphasis: "",
      lead: false,
    },
  ],
  /**
   * STATUS VERIFIED 2026-08-19. The source called this "the only content on the
   * site with a shelf life" and asked for a check before publishing. Done — and
   * `regulatory` above is accurate on every point:
   *
   *   - FDA finalised "Computer Software Assurance for Production and Quality
   *     System Software" on 24 September 2025. It supplements the 2002 General
   *     Principles of Software Validation and supersedes that document's
   *     Section 6.
   *   - On 3 February 2026 FDA released an updated final guidance, retitled
   *     "...Production and Quality Management System Software", which
   *     SUPERSEDES the September 2025 version and aligns to QMSR. It cites
   *     ISO 13485:2016 subclauses 4.1.6, 7.5.6 and 7.6 explicitly.
   *   - QMSR took effect 2 February 2026, replacing the Quality System
   *     Regulation and incorporating ISO 13485:2016 by reference.
   *
   * One nuance the page does not draw out, and does not need to: February 2026
   * SUPERSEDED September 2025 rather than merely updating it. "Issued an updated
   * version" is accurate; "superseding" would be sharper. Client copy, left
   * alone.
   *
   * **RE-CHECK STAYS QUARTERLY — next due November 2026** — and this section
   * remains on CLAUDE.md's launch-blocking list. Two guidance revisions inside
   * six months is why.
   *
   * Do not soften `clarifications[0]`, "What CSA is not". Every competitor
   * sells CSA as cost reduction; this page says plainly that it is not, and the
   * research supports it — CSA redirects effort by risk, it does not reduce it.
   */
} as const;

/**
 * §4 — the wedge, and the source calls it narrow and time-limited. AI systems
 * entered CSA scope in February 2026; pharma quality organisations have an
 * obligation and no method.
 */
export const ai = {
  display: "Where AI enters, and why it matters now",
  scope:
    "The February 2026 guidance explicitly brings automation tools, data analytics, and AI/ML systems into CSA scope where they're used for production or quality purposes.",
  problem:
    "That creates an obligation most validation programmes have no method for. An AI tool used in a quality process is non-deterministic, updates without your involvement if it's SaaS, and doesn't produce the same output twice — which is awkward for an approach built on scripted expected results.",
  answer:
    "We work on both sides of that. We assure AI systems entering your quality processes, and we use AI in the assurance work itself — requirement analysis, risk assessment support, and test generation — under the same rationale we'd defend to an inspector.",
  close:
    "If you're adopting AI in a GxP context and your validation approach hasn't been asked to handle it yet, it will be.",
} as const;

export type Capability = { lead: string; tail: string; icon: IconName };

export const capabilities = {
  display: "What we do",
  items: [
    {
      lead: "Risk assessment and rationale.",
      tail: "Intended use, process risk, and the assurance activity that follows from it — documented so the decision is defensible rather than personal.",
      icon: "target",
    },
    {
      lead: "Proportionate assurance.",
      tail: "Scripted testing where process risk warrants it. Scenario and exploratory testing where it doesn't, with the reasoning recorded.",
      icon: "gauge",
    },
    {
      lead: "Part 11 controls verification.",
      tail: "Audit trail integrity, electronic signature binding, access control, and ALCOA+ data integrity across the record lifecycle.",
      icon: "shieldCheck",
    },
    {
      lead: "Vendor evidence assessment.",
      tail: "Deciding what supplier documentation you can rely on, and what you genuinely need to re-test. This is where most of the wasted effort in traditional CSV sits.",
      icon: "search",
    },
    {
      lead: "SOP alignment.",
      tail: "A CSA-based approach that contradicts your validation SOP helps nobody. We work on the procedure alongside the practice.",
      icon: "fileText",
    },
    {
      lead: "Maintaining the validated state.",
      tail: "Assurance is a lifecycle, not an event. Change assessment that doesn't restart the whole exercise every release.",
      icon: "infinity",
    },
  ] as Capability[],
} as const;

export const systems = {
  display: "Systems we validate — and don't",
  doLabel: "We do",
  doItems: [
    "eQMS and electronic document management",
    "ERP in GxP scope",
    "Clinical systems (CTMS, EDC, eTMF)",
  ],
  dontLabel: "We don't",
  dontItems: [
    "LIMS",
    "Manufacturing execution and production floor systems",
    "Software that is itself a medical device",
  ],
  note: "That second list is deliberate. Shop-floor and laboratory systems need specialists with that domain, and SaMD follows a different regulatory route entirely — CSA explicitly excludes it.",
  precision: {
    label: "One point of precision on clinical systems.",
    body: " CSA is scoped to production and quality system software. CTMS, EDC and eTMF sit under GCP and Part 11 rather than inside CSA's formal scope. The risk-based reasoning transfers cleanly and we apply it — but we won't tell you the guidance covers them when it doesn't.",
  },
} as const;

/**
 * §7 — the weakest section, and honest about why. The source calls volunteering
 * that pharma is newer for WeAreQA a net gain in a sector where overstating
 * regulatory experience is the cardinal sin. No case study: the client chose to
 * leave the adjacent manufacturing study out.
 */
export const experience = {
  display: "Where our experience comes from",
  /**
   * Filled from the Webflow export supplied with case-studies-legacy-seven.md.
   * These are engagement shapes the client confirmed as this team's own track
   * record, so the placeholder that stood here is no longer needed.
   */
  engagementsLabel: "Delivered by this team",
  engagements: [
    { months: "12", team: "9 QA engineers", region: "US", work: "Customer engagement platform — compliance, security and functional testing" },
    { months: "12", team: "5 QA engineers", region: "US", work: "Healthcare AI analytics framework — data quality across the processing pipeline" },
    { months: "4", team: "3 QA engineers", region: "US", work: "Sample management platform — formal IQ/OQ, API and data quality testing" },
  ],  statement:
    "Our validation work has been on quality and business systems in GxP scope — eQMS and document management, ERP, and clinical systems — under 21 CFR Part 11 and GAMP 5 categorisation.",
  candour: {
    strong:
      "Three pharma and life-science engagements, delivered by this team.",
    tail: " Ask on the first call about who would do the work and what they have done before, and we will give you a straight answer."
  },
} as const;

export const businessCase = {
  display: "The business case",
  note: "For the budget conversation.",
  columnLabel: "Cost of staying with full CSV",
  rows: [
    {
      label: "Effort",
      cost: "Uniform documentation applied to systems that don't warrant it",
      icon: "clock",
    },
    {
      label: "Change avoidance",
      cost: "Revalidation cost makes teams avoid upgrades, so systems age past support",
      icon: "alert",
    },
    {
      label: "Digital initiatives",
      cost: "Cloud, SaaS and AI adoption stalls behind a validation approach that can't keep pace",
      icon: "rocket",
    },
    {
      label: "Inspection readiness",
      cost: "Volume of documentation is not the same as quality of evidence, and inspectors know the difference",
      icon: "search",
    },
  ] as { label: string; cost: string; icon: IconName }[],
  close:
    "The saving isn't in doing less work. It's in stopping the work that was never proportionate to the risk, and putting that effort where an inspector would actually look.",
} as const;

export const notFit = {
  display: "Where we're not the right fit",
  items: [
    { lead: "Manufacturing execution and LIMS.", tail: " Not our systems." },
    {
      lead: "SaMD and medical device software.",
      tail: " Different regulatory route, needs a device specialist.",
    },
    {
      lead: "Anyone wanting CSA framed as a documentation reduction exercise.",
      tail: " That's a misreading, and we'd rather lose the work than write a rationale we couldn't defend.",
    },
  ],
} as const;

/**
 * NO PRICE, BY DECISION — consistent with the four service pages and the other
 * four industry pages. This was the last price placeholder on the site.
 *
 * **WHAT IS LEFT IS A HEADING AND TWO BUTTONS.** No body copy; the placeholder
 * was the only thing between "Starting from" and the CTAs, so the section
 * announces a price and shows a call to action instead of one. It was always
 * closer to a closing CTA than a pricing section: centred, yellow accent bar,
 * the same two buttons the industries hub uses to close.
 *
 * ALL FIVE INDUSTRY PAGES ARE IN THIS STATE, and the client has decided to
 * leave them that way (2026-08-19). Do not "fix" them.
 */
export const pricing = {
  display: "Starting from",
} as const;

export const related = {
  display: "Related",
  items: [
    {
      label: "Healthcare & HealthTech",
      tail: "Digital health products",
      href: "/industries/healthcare",
    },
    {
      label: "QA Consulting & Modernization",
      tail: "Assessment of your current validation approach",
      href: "/services/qa-consulting-modernization",
    },
    { label: "AI-Driven Validation", tail: "", href: "/services/ai-driven-validation" },
    {
      label: "AI-Native Delivery Framework",
      tail: "",
      href: "/services/ai-driven-validation#framework-heading",
    },
  ],
} as const;
